/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description ClaimWorkListActions
 */
import _ from 'lodash';
import { Dropdown, Tooltip } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { MoreOne } from '@icon-park/react';

import Button from 'components/widgets/button';
import useWriteOffPR from 'hooks/mutate/useWriteOffPR';
import { defaultDropdownButtonProps } from './ClaimTableActions';
import { getClaimActions } from 'utils/claims/claimState';
import { useAppStoreValues } from 'store/app';
import { useClaimPostPaymentStoreActions } from 'store/claimPostPayment';
import { CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { useFilterSchema } from '../AccessControl';
import { hasPrintableClaimResponseItems } from './PrintClaimResponse';

export interface IProps {
	claimId: string;
	data: ClaimRequest.IClaim;
	paymentAllocationData: ClaimStatusResponse.IClaimPaymentReconciliation;
	patientName?: string;
	isEligibilitySupported: boolean;
	isClaimStatusSupported: boolean;
	isERAPresent: boolean;
	isSecondaryFiled: boolean;
	isSecondaryClaim: boolean;
	exportItems?: ClaimStatusResponse.IExportSchema[];
	activityTypes: ClaimRequest.IActivityType[];
	patientId?: string;
	tenantPayers?: Insurance.ITenantPayerOption[];
	isArchivedClaim?: boolean;
	isVoidedClaim?: boolean;
	isDraftClaim?: boolean;
	isDraftPostAvailable?: boolean;
	isPrimaryClaimVoided?: boolean;
	isVoidProcessing?: boolean;
	isVoidSupersededClaim?: boolean;
	showPostPaymentVoidConditions?: boolean;
	isValidLocation: boolean;
	metadata: Metadata.IResponse;
}

const getDisabledActionTooltip = (
	isArchivedClaim?: boolean,
	isVoidProcessing?: boolean,
	isVoidedClaim?: boolean,
	isVoidSupersededClaim?: boolean,
): string => {
	if (isArchivedClaim) {
		return 'Actions are disabled as this claim has been archived.';
	}
	if (isVoidProcessing) {
		return 'Actions are disabled while this void request is being processed.';
	}
	if (isVoidedClaim) {
		return 'Actions are disabled because this claim has been voided.';
	}
	if (isVoidSupersededClaim) {
		return 'Actions are disabled because a void claim has been filed and this claim is now superseded.';
	}
	return '';
};

const ClaimActions: React.FC<IProps> = ({
	claimId,
	data,
	paymentAllocationData,
	isEligibilitySupported,
	isClaimStatusSupported,
	activityTypes,
	isERAPresent,
	isSecondaryFiled,
	isSecondaryClaim,
	patientName,
	exportItems,
	tenantPayers,
	patientId,
	isArchivedClaim,
	isVoidedClaim,
	isDraftClaim,
	isDraftPostAvailable,
	isPrimaryClaimVoided,
	isVoidProcessing,
	isVoidSupersededClaim,
	showPostPaymentVoidConditions,
	isValidLocation,
	metadata,
}) => {
	const claimStatusRequestId = data?.metadata?.claimResponseIds?.x12_277;

	const { mutateAsync, isPending } = useWriteOffPR();
	const pendingPR = data?.metadata?.pendingPR ?? 0;
	const { userAccess, features } = useAppStoreValues();
	const { requestOpen: requestOpenPostPayment } = useClaimPostPaymentStoreActions();
	const canEditClaimStatus = !!features?.claims?.statusapi?.independent;
	const payerMeta = _.find(metadata?.selectedPayers, { id: data?.claimStatusRequestResponse?.requestInfo?.tenantpayerId });
	const providerMeta = _.get(metadata?.providerMetaData, [data?.claimStatusRequestResponse?.requestInfo?.providerId]);

	const { changeWorkState, modifyClaimWorklist, addActivity, resubmitClaim, fileSecondaryClaim, allActions, postPayment } =
		getClaimActions({
			workState: data?.metadata?.workState,
			pendingPR,
			userAccess,
			isDraftPostAvailable,
			hasManualClaim: !!data?.fhirClaimResponses?.post_manual,
		});

	const schema: (MenuItemType & { permissions?: (keyof App.UserAccess)[] })[] = [
		postPayment.visible &&
			postPayment.location === 'dropdown' && {
				key: 'postPayment',
				className: 'tw-dropdown-menu-item',
				label: (
					<Tooltip title={postPayment.tooltip}>
						<Button.PostPaymentButton
							shouldShow
							showPostPaymentVoidConditions={showPostPaymentVoidConditions}
							{...defaultDropdownButtonProps}
							disabled={postPayment.disabled}
							onClick={() => requestOpenPostPayment(claimId)}
						/>
					</Tooltip>
				),
			},
		changeWorkState?.visible && {
			key: 'claimWorkState',
			className: 'tw-dropdown-menu-item',
			disabled: changeWorkState?.disabled,
			permissions: ['canCloseClaim'],
			label: (
				<Tooltip title={changeWorkState?.tooltip}>
					<Button.ChangeClaimWorkState
						workState={data?.metadata?.workState}
						previousWorkState={data?.metadata?.previousWorkState}
						claimId={claimId}
						claimResource={data?.fhirResource}
						disabled={changeWorkState?.disabled}
						{...defaultDropdownButtonProps}
					/>
				</Tooltip>
			),
		},
		{
			key: 'refreshClaimStatus',
			className: 'tw-dropdown-menu-item',
			label: (
				<Button.ResubmitClaimRequest
					claimId={claimId}
					isClaimPayer={isClaimStatusSupported}
					isTextButton={true}
					label={'Refresh Claim Status'}
					workState={data?.metadata?.workState}
					{...defaultDropdownButtonProps}
				/>
			),
		},
		canEditClaimStatus && {
			key: 'editClaimStatusCheck',
			className: 'tw-dropdown-menu-item',
			label: (
				<Button.EditClaimRequest
					// TODO : later revert to data?.fhirClaimResponses?.x12_277
					id={data?.claimStatusRequestResponse?.fhirClaimResponse?.id}
					payer={payerMeta}
					providerMeta={providerMeta}
					isValidLocation={isValidLocation}
					label={'Edit Claim Status Check'}
					{...defaultDropdownButtonProps}
				/>
			),
		},
		{
			key: 'viewEligibilityRequestChecks',
			className: 'tw-dropdown-menu-item',
			label: (
				<Button.ViewEligibilityRequestChecks
					isEligibilityPayer={isEligibilitySupported}
					claimStatusRequestId={claimStatusRequestId}
					patientId={patientId}
					{...defaultDropdownButtonProps}
				/>
			),
		},
		hasPrintableClaimResponseItems(exportItems) && {
			key: 'exportClaimData',
			className: 'tw-dropdown-menu-item',
			permissions: ['canPrintClaimResponse'],
			label: <Button.PrintClaimResponse patientName={patientName} exportItems={exportItems} {...defaultDropdownButtonProps} />,
		},
		modifyClaimWorklist?.visible && {
			key: 'addToManualWorkList',
			className: 'tw-dropdown-menu-item',
			permissions: ['hasWorklistAccess'],
			label: (
				<Tooltip title={modifyClaimWorklist?.tooltip}>
					<Button.AddToManualWorkList
						{...defaultDropdownButtonProps}
						disabled={modifyClaimWorklist?.disabled}
						claimId={claimId}
						data={data}
						label={'Modify Claim Worklist(s)'}
					/>
				</Tooltip>
			),
		},
		resubmitClaim?.visible &&
			!isSecondaryFiled &&
			isERAPresent && {
				key: 'resubmitClaimRequest',
				className: 'tw-dropdown-menu-item',
				label: (
					<Tooltip title={resubmitClaim?.tooltip}>
						<Button.ResubmitClaim id={claimId} {...defaultDropdownButtonProps} disabled={resubmitClaim?.disabled} />
					</Tooltip>
				),
			},
		addActivity?.visible && {
			key: 'addNote',
			className: 'tw-dropdown-menu-item',
			disabled: addActivity?.disabled,
			label: (
				<Tooltip title={addActivity?.tooltip}>
					<Button.AddNote
						activityTypes={activityTypes}
						claimId={claimId}
						disabled={addActivity?.disabled}
						{...defaultDropdownButtonProps}
					/>
				</Tooltip>
			),
		},
		fileSecondaryClaim?.visible &&
			!isSecondaryFiled &&
			!isSecondaryClaim && {
				key: 'fileSecondaryClaim',
				className: 'tw-dropdown-menu-item',
				permissions: ['canFileClaims'],
				label: (
					<Tooltip title={fileSecondaryClaim?.tooltip}>
						<Button.FileSecondaryClaim
							claimId={claimId}
							patientId={patientId}
							tenantPayers={tenantPayers}
							{...defaultDropdownButtonProps}
							disabled={fileSecondaryClaim?.disabled}
						/>
					</Tooltip>
				),
			},
		{
			key: 'voidClaim',
			danger: true,
			className: 'tw-dropdown-menu-item',
			permissions: ['hasVoidClaimAccess'],
			label: (
				<Button.VoidClaim
					claimId={claimId}
					claimData={data}
					paymentAllocationData={paymentAllocationData}
					{...defaultDropdownButtonProps}
				/>
			),
		},
		data?.metadata?.workState === CLAIM_WORK_STATES.a_payment_posted.value &&
			pendingPR && {
				key: 'writeOffClaim',
				className: 'tw-dropdown-menu-item',
				permissions: ['canWriteOffClaim'],
				label: (
					<Button.WriteOff
						{...defaultDropdownButtonProps}
						writeOffData={{ amount: pendingPR, reason: null }}
						claimId={claimId}
						isPending={isPending}
						onWriteOffChange={(value) => {
							mutateAsync({
								claims: [
									{
										claimId,
										claimVersionId: data?.fhirResource?.meta?.versionId,
										writeOffAmount: null,
										writeOffReasonId: value?.reason,
										writeOffAllPending: true,
									},
								],
							});
						}}
					/>
				),
			},
	];

	const filteredSchema = useFilterSchema(schema);

	if (allActions.disabled) {
		return (
			<Tooltip title={allActions?.tooltip}>
				<Button
					disabled={allActions?.disabled}
					type='default'
					icon={<MoreOne size={20} className='pt-0.5' />}
					onClick={(e) => e.stopPropagation()}
					iconPosition='end'
					data-testid='rsb-claim-kebab'
				/>
			</Tooltip>
		);
	}

	if (isDraftClaim) {
		return null;
	}

	return (
		<Dropdown menu={{ items: filteredSchema }} trigger={['click']} overlayClassName='claim-actions-dropdown'>
			<Tooltip
				placement='bottomRight'
				title={getDisabledActionTooltip(isArchivedClaim, isVoidProcessing, isVoidedClaim, isVoidSupersededClaim)}
			>
				<Button
					type='default'
					icon={<MoreOne size={20} style={{ paddingTop: 2 }} />}
					onClick={(e) => e.stopPropagation()}
					iconPosition='end'
					disabled={isArchivedClaim || isVoidedClaim || isVoidProcessing || isPrimaryClaimVoided || isVoidSupersededClaim}
					data-testid='rsb-claim-kebab'
				/>
			</Tooltip>
		</Dropdown>
	);
};

export default ClaimActions;
